import { useState, useEffect } from "react";
import { useForm, Controller, FormProvider } from "react-hook-form";
import AccessPanel from "admin/components/user-management/AccessPanel";
import AdminGrantsPanel from "admin/components/user-management/AdminGrantsPanel";
import ConfirmGrantChangesDialog from "admin/components/user-management/ConfirmGrantChangesDialog";
import FunctionalRoleSelector from "admin/components/user-management/FunctionalRoleSelector";
import GroupSelector from "admin/components/user-management/GroupSelector";
import { getStatusBadgeVariant } from "admin/components/user-management/UserTable";
import useCanEditAdminApp from "admin/hooks/useCanEditAdminApp";
import { useCanViewAdminGrants } from "admin/hooks/useCanViewAdminGrants";
import { usePendingGrants } from "admin/hooks/usePendingGrants";
import useUserAccess from "admin/hooks/useUserAccess";
import useUsers from "admin/hooks/useUsers";
import { GrantListResponse, GrantPermission } from "admin/types/interfaces/grant";
import User, { UserRole, UserGroup } from "admin/types/interfaces/user";
import { excludeSystemTags } from "admin/utils/tags";
import { add, differenceInHours, format } from "date-fns";
import { User as UserIcon, Calendar, Loader2, Mail, Info } from "lucide-react";
import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "~/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { ACCESS_CONTROLLER_V1 } from "~/utils/accessController";
import { extractErrorMessage } from "~/utils/apiClient";
import { trackEvent } from "~/utils/monitoring";

const { VITE_ACCESS_CONTROLLER_API } = import.meta.env;

type UserDrawerTab = "roles" | "groups" | "access" | "administration";

interface UserDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  defaultTab?: UserDrawerTab;
}

interface UpdateUserOptions {
  full_name?: string;
  is_active?: boolean;
  roles?: string[];
  tags?: string[];
}

interface UserFormData {
  full_name: string;
  is_active: boolean;
  roles: UserRole[];
  tags: UserGroup[];
}

const UserStatusOptions = [
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
];

const formatInviteLastSentAt = (inviteLastSentAt?: string) => {
  if (!inviteLastSentAt) {
    return { datePart: "", timePart: "" };
  }
  const date = new Date(inviteLastSentAt);
  const nextAvailableDate = add(date, { hours: 24 }); // 24 hours from invite last sent at
  return {
    datePart: format(nextAvailableDate, "M/d/yyyy 'at' "),
    timePart: format(nextAvailableDate, "h:mm a"),
  };
};

const isWithinLast24Hours = (inviteLastSentAt: string) => {
  const lastSent = new Date(inviteLastSentAt);
  const now = new Date();
  return differenceInHours(now, lastSent) < 24;
};

export function UserDrawer({ isOpen, onClose, user, defaultTab = "roles" }: UserDrawerProps) {
  const [activeTab, setActiveTab] = useState<UserDrawerTab>(defaultTab);
  const { showError, showSuccess } = useToast();
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showGrantConfirmDialog, setShowGrantConfirmDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResendInviteDialog, setShowResendInviteDialog] = useState(false);
  const [isResendingInvite, setIsResendingInvite] = useState(false);
  const { invite_last_sent_at, auth0 } = user;
  const canEdit = useCanEditAdminApp();
  const canEditUser = user.actions_permitted
    ? user.actions_permitted.includes(GrantPermission.can_edit)
    : canEdit;
  const canManage = user.actions_permitted
    ? user.actions_permitted.includes(GrantPermission.can_manage)
    : canEdit;
  const showAdminGrants = useCanViewAdminGrants();
  const hasNotLoggedIn = auth0?.last_login === null || auth0?.last_login === undefined;
  const canResendInvite = invite_last_sent_at ? !isWithinLast24Hours(invite_last_sent_at) : true;
  const showResendInviteAlert = hasNotLoggedIn && canEdit;
  const { datePart, timePart } = formatInviteLastSentAt(invite_last_sent_at);

  const { mutate } = useUsers();

  const {
    data: accessData,
    isLoading: accessDataIsLoading,
    error: accessDataError,
  } = useUserAccess(user.email);

  const { mutate: mutateGrants } = useFetcher<GrantListResponse>(
    `${VITE_ACCESS_CONTROLLER_API}/users/${user.email}/grants`
  );

  const {
    pendingGrantAdditions,
    pendingGrantRemovals,
    pendingGrantUpdates,
    hasGrantChanges,
    resetGrants,
    onAddAccess,
    onRemoveAccess,
    onUpdateAccess,
    flushGrants,
  } = usePendingGrants();

  const form = useForm<UserFormData>({
    defaultValues: {
      full_name: user.full_name,
      is_active: user.is_active,
      roles: user.roles,
      tags: user.tags,
    },
    mode: "onChange",
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty, dirtyFields, errors },
  } = form;

  // Reset tab and pending grants when drawer opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab === "administration" && !showAdminGrants ? "roles" : defaultTab);
      resetGrants();
    }
  }, [isOpen, defaultTab, resetGrants]);

  const onSubmit = async (data: UserFormData) => {
    trackEvent("admin_user_save_clicked");
    setIsSubmitting(true);

    const apiPayload: UpdateUserOptions = {};

    if (dirtyFields.full_name) {
      apiPayload.full_name = data.full_name;
    }
    if (dirtyFields.is_active) {
      apiPayload.is_active = data.is_active;
    }
    if (dirtyFields.roles) {
      apiPayload.roles = data.roles.map((role) => role.id);
    }
    if (dirtyFields.tags) {
      const manualTags = excludeSystemTags(data.tags);
      apiPayload.tags = manualTags.map((tag) => tag.name);
    }

    const grantsUrl = `${VITE_ACCESS_CONTROLLER_API}/users/${user.email}/grants`;

    if (Object.keys(apiPayload).length > 0) {
      const url = `${ACCESS_CONTROLLER_V1}/users/${user.email}`;
      const response = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(apiPayload),
      });
      if (!response.ok) {
        setIsSubmitting(false);
        const body = await response.json().catch(() => null);
        showError(extractErrorMessage(body) ?? "Failed to update user, please try again");
        return;
      }
    }

    const { failedLabels } = await flushGrants(grantsUrl);

    await mutate();
    await mutateGrants();
    setIsSubmitting(false);

    if (failedLabels.length > 0) {
      showError(`Failed to save grants for: ${failedLabels.join(", ")}`);
    } else {
      showSuccess("User updated successfully");
      onClose();
    }
  };

  // Administration-tab grant changes need an explicit confirmation before saving
  const onValidSubmit = (data: UserFormData) =>
    hasGrantChanges ? setShowGrantConfirmDialog(true) : onSubmit(data);

  const handleClose = () => {
    // Don't close if the resend invite or grant confirmation dialog is open
    if (showResendInviteDialog || showGrantConfirmDialog) {
      return;
    }
    if ((isDirty || hasGrantChanges) && canEditUser) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedDialog(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowUnsavedDialog(false);
  };

  const handleResendInvite = async () => {
    setIsResendingInvite(true);
    const url = `${ACCESS_CONTROLLER_V1}/users/${user.email}/send-invite`;
    const response = await fetch(url, { method: "POST" });
    if (!response.ok) {
      setIsResendingInvite(false);
      showError("Failed to resend invite, please try again");
      return;
    }
    await mutate();
    showSuccess("Invite sent successfully");
    setIsResendingInvite(false);
    setShowResendInviteDialog(false);
    onClose();
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleClose} modal={false}>
        <SheetContent
          className="w-[750px] sm:max-w-[750px] bg-background flex flex-col h-screen gap-0"
          onOpenAutoFocus={(e) => {
            // Prevent auto-focus on the active tab trigger
            e.preventDefault();
          }}
        >
          <form onSubmit={handleSubmit(onValidSubmit)} className="flex flex-col h-full">
            <SheetHeader className="flex-shrink-0 pl-8 pr-16">
              <SheetTitle className="flex items-center gap-3 text-2xl">
                <UserIcon className="h-5 w-5" />
                {user.full_name}
              </SheetTitle>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={getStatusBadgeVariant(user.is_active)}>
                  {user.is_active ? "Enabled" : "Disabled"}
                </Badge>
              </div>
            </SheetHeader>

            {showResendInviteAlert && (
              <div className="px-8 mb-4">
                <Alert className="w-full bg-blue-50 border-none items-center [&>svg]:translate-y-0">
                  <Info className="size-4 shrink-0 fill-blue-600 stroke-white" />
                  <AlertDescription className="text-blue-600 flex items-center justify-between">
                    <span>
                      <strong>User pending.</strong>{" "}
                      {canResendInvite ? (
                        <>This user has been created but hasn&apos;t logged in yet.</>
                      ) : (
                        <span>
                          Invites are limited to once every 24 hours. You can resend after{" "}
                          {datePart}
                          <span className="whitespace-nowrap">{timePart}</span>.
                        </span>
                      )}
                    </span>
                    {canResendInvite && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowResendInviteDialog(true)}
                        className="bg-background hover:bg-muted"
                      >
                        <Mail className="h-4 w-4" />
                        Resend Invite
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              </div>
            )}

            <FormProvider {...form}>
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="px-8 pb-4 shrink-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Controller
                        name="full_name"
                        control={control}
                        rules={{ required: "Full name is required" }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            disabled={!canEditUser}
                            className="bg-background border-border"
                          />
                        )}
                      />
                      {errors.full_name && (
                        <p className="text-sm text-destructive">{errors.full_name.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input value={user.email} disabled className="bg-background border-border" />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Controller
                        name="is_active"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value ? "enabled" : "disabled"}
                            onValueChange={(value) => field.onChange(value === "enabled")}
                            disabled={!canEditUser}
                          >
                            <SelectTrigger className="bg-background border-border">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {UserStatusOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Created on</Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                          value={new Date(user.provisioned_at || "").toLocaleDateString()}
                          disabled
                          className="bg-background border-border pl-10"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Tabs
                  value={activeTab}
                  onValueChange={(value) => setActiveTab(value as UserDrawerTab)}
                  className="w-full flex-1 flex flex-col overflow-hidden"
                >
                  <div className="mx-8 mt-2">
                    <TabsList>
                      <TabsTrigger value="roles">Functional Roles</TabsTrigger>
                      <TabsTrigger value="groups">Groups</TabsTrigger>
                      <TabsTrigger
                        value="access"
                        onClick={() =>
                          trackEvent("admin_access_tab_clicked", {
                            type: "user",
                          })
                        }
                      >
                        Access
                      </TabsTrigger>
                      {showAdminGrants && (
                        <TabsTrigger value="administration">Administration</TabsTrigger>
                      )}
                    </TabsList>
                  </div>
                  <div className="flex-1 overflow-y-auto px-8 pb-6">
                    <TabsContent value="roles" className="mt-2">
                      <Controller
                        name="roles"
                        control={control}
                        render={({ field }) => (
                          <FunctionalRoleSelector
                            selectedRoles={field.value}
                            setSelectedRoles={field.onChange}
                            disabled={!canEditUser}
                          />
                        )}
                      />
                    </TabsContent>
                    <TabsContent value="groups" className="mt-2">
                      <Controller
                        name="tags"
                        control={control}
                        render={({ field }) => (
                          <GroupSelector
                            selectedGroups={field.value}
                            setSelectedGroups={field.onChange}
                            disabled={!canEditUser}
                          />
                        )}
                      />
                    </TabsContent>
                    <TabsContent value="access" className="mt-2 space-y-6">
                      <AccessPanel
                        accessData={accessData}
                        isLoading={accessDataIsLoading}
                        error={accessDataError}
                      />
                    </TabsContent>
                    {showAdminGrants && (
                      <TabsContent value="administration" className="mt-2">
                        <AdminGrantsPanel
                          grantsUrl={`${VITE_ACCESS_CONTROLLER_API}/users/${user.email}/grants`}
                          resourceType="user"
                          pendingAdditions={pendingGrantAdditions}
                          pendingRemovals={pendingGrantRemovals}
                          pendingUpdates={pendingGrantUpdates}
                          onAddAccess={onAddAccess}
                          onRemoveAccess={onRemoveAccess}
                          onUpdateAccess={onUpdateAccess}
                          canEdit={canManage && canEdit}
                        />
                      </TabsContent>
                    )}
                  </div>
                </Tabs>
              </div>
            </FormProvider>

            {canEditUser && (
              <div className="flex-shrink-0 bg-background border-t border-border py-4 px-8">
                <div className="flex items-center justify-end gap-2">
                  {(isDirty || hasGrantChanges) && (
                    <Button variant="outline" type="button" onClick={handleClose}>
                      Cancel
                    </Button>
                  )}
                  <Button disabled={(!isDirty && !hasGrantChanges) || isSubmitting} type="submit">
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </SheetContent>
      </Sheet>

      <ConfirmGrantChangesDialog
        open={showGrantConfirmDialog}
        resourceLabel={user.email}
        additions={pendingGrantAdditions}
        removals={pendingGrantRemovals}
        updates={pendingGrantUpdates}
        onConfirm={() => handleSubmit(onSubmit)()}
        onClose={() => setShowGrantConfirmDialog(false)}
      />

      <AlertDialog open={showUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmClose}
              className="bg-destructive hover:bg-destructive/90"
            >
              Close without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showResendInviteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resend Invitation Email</AlertDialogTitle>
            <AlertDialogDescription>
              This will send a new invitation email to <strong>{user.email}</strong>. <br /> <br />
              Please confirm the following:{" "}
              <ul className="list-disc list-inside ml-4">
                <li>Any previous invitation links will stop working immediately.</li>
                <li>You will need to wait 24 hours before you can send another invite.</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowResendInviteDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResendInvite}
              disabled={isResendingInvite}
              className="bg-primary hover:bg-primary/90"
            >
              {isResendingInvite ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Invitation"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
